import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type ComponentType,
} from "react";
import { Empty, Pagination, Select, Spin, message } from "antd";
import type { TablePaginationConfig } from "antd/es/table";
import type { FilterValue, SorterResult } from "antd/es/table/interface";
import { DownloadOutlined } from "@ant-design/icons";
import { ChevronDown, ChevronUp, Filter } from "react-feather";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import CustomDatePicker from "../../CommonComponents/CustomDatePicker/CustomDatePicker";
import DataTableBase from "../../shared/dashboards/DataTable/DataTable";
import Input from "../../shared/Input/Input";
import SharedSelect from "../../shared/Select/Select";
import AppContext from "../../../context/AppContext";
import { SIDER_TABS } from "../../../utils/Constants";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { getEffectiveAdminTimezone } from "../../../utils/dateService";
import { buildCasinoProvidersSortParam } from "../../../queries/casino/casinoProvidersReportApi";
import { useCasinoProvidersReport } from "../../../queries/casino/useCasinoProvidersReport";
import { normalizeAntSelectNumberValue } from "../../ReportingSummary/reportingSummaryUtils";
import { PAGE_SIZE_OPTIONS } from "../../ReportingSummary/reportingSummaryConstants";
import { parseCasinoKpiFilterMetadata } from "../../../queries/casino-kpis/casinoKpiFilters";
import {
  type CasinoProviderRow,
  type CasinoProviderSortKey,
  getCasinoProvidersColumns,
} from "./casinoProvidersColumns";
import { useCurrencySymbol } from "../../../hooks/useCurrencySymbol";
import { usePageTitle } from "../../CommonComponents/Layout/PageTitleContext";

import "./stylesCasinoProvidersReports.css";

dayjs.extend(utc);
dayjs.extend(timezone);

const DataTable = DataTableBase as ComponentType<Record<string, unknown>>;

const DEFAULT_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 400;

function escapeCsvCell(value: string | number): string {
  const s = String(value);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function getDefaultProvidersDateRange(): [Dayjs, Dayjs] {
  const tz = getEffectiveAdminTimezone();
  return [
    dayjs().tz(tz).startOf("month"),
    dayjs().tz(tz).endOf("day"),
  ];
}

/** CSV for the current page of provider rows (no backend export yet). */
function buildCasinoProvidersCsv(rows: CasinoProviderRow[]): string {
  const header = [
    "name",
    "uniquePlayers",
    "rounds",
    "avgRounds",
    "turnover",
    "wins",
    "rtpPct",
    "ggr",
    "avgBet",
  ] as const;
  const lines = [header.join(",")];
  for (const r of rows) {
    lines.push(
      header
        .map((key) =>
          escapeCsvCell(r[key as keyof CasinoProviderRow] as string | number),
        )
        .join(","),
    );
  }
  return lines.join("\n");
}

function CasinoProvidersReports() {
  usePageTitle(AppStrings.CASINO_PROVIDERS_PAGE_TITLE);
  const { setActiveSidebarTab } = useContext(AppContext);

  const [dates, setDates] = useState<[Dayjs, Dayjs]>(getDefaultProvidersDateRange);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [draftMoneyTypes, setDraftMoneyTypes] = useState<string[]>([]);
  const [appliedMoneyTypes, setAppliedMoneyTypes] = useState<string[]>([]);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [sortState, setSortState] = useState<{
    field: CasinoProviderSortKey | null;
    order: "ascend" | "descend" | null;
  }>({ field: null, order: null });

  useEffect(() => {
    const t = setTimeout(
      () => setDebouncedSearch(searchText.trim()),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(t);
  }, [searchText]);

  const startDate = dates[0]?.format("YYYY-MM-DD");
  const endDate = dates[1]?.format("YYYY-MM-DD");

  const sortParam = useMemo(() => {
    if (!sortState.field || !sortState.order) {
      return undefined;
    }
    return buildCasinoProvidersSortParam(
      sortState.field,
      sortState.order,
    );
  }, [sortState.field, sortState.order]);

  const reportParams = useMemo(
    () => ({
      start_date: startDate,
      end_date: endDate,
      page: currentPage,
      size: pageSize,
      search: debouncedSearch,
      sort: sortParam,
      money_type: appliedMoneyTypes,
    }),
    [
      startDate,
      endDate,
      currentPage,
      pageSize,
      debouncedSearch,
      sortParam,
      appliedMoneyTypes,
    ],
  );

  const queryEnabled = Boolean(startDate && endDate);

  const { data, isError, isFetching } = useCasinoProvidersReport(
    reportParams,
    {
      enabled: queryEnabled,
    },
  );

  useEffect(() => {
    if (isError) {
      message.error(AppStrings.CASINO_PROVIDERS_LOAD_ERROR);
    }
  }, [isError]);

  const displayRows = useMemo(
    (): CasinoProviderRow[] => (data?.rows ?? []) as CasinoProviderRow[],
    [data?.rows],
  );

  const displayTotal = data?.total ?? 0;
  const currencyTypeOptions = useMemo(
    () => parseCasinoKpiFilterMetadata(data?.meta).options,
    [data?.meta],
  );
  const filtersDirty =
    JSON.stringify(draftMoneyTypes) !== JSON.stringify(appliedMoneyTypes);

  const tableEmptyText = useMemo((): string => {
    if (isError) {
      return AppStrings.NO_DATA;
    }
    const emptyText =
      data && typeof data === "object" && "emptyText" in data
        ? (data as { emptyText?: string | null }).emptyText
        : null;
    if (typeof emptyText === "string" && emptyText.trim().length > 0) {
      return emptyText.trim();
    }
    return AppStrings.NO_DATA;
  }, [isError, data]);

  /**
   * Ant Design Table: if `locale.emptyText` is a string, it is rendered as plain text (no icon).
   * Reporting Summary uses the default, which is `<Empty image={PRESENTED_IMAGE_SIMPLE} />`.
   * We pass the same `Empty` + `description` so the Providers table matches sportsbook.
   */
  const tableEmptyNode = useMemo(
    () => (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={tableEmptyText}
      />
    ),
    [tableEmptyText],
  );

  useEffect(() => {
    setActiveSidebarTab(SIDER_TABS.CASINO, "PROVIDERS");
  }, [setActiveSidebarTab]);

  const currencySymbol = useCurrencySymbol() ?? "$";
  const columns = useMemo(
    () => getCasinoProvidersColumns(sortState, currencySymbol),
    [sortState, currencySymbol],
  );

  const handleTableChange = useCallback(
    (
      _pagination: TablePaginationConfig,
      _filters: Record<string, FilterValue | null>,
      sorter:
        | SorterResult<CasinoProviderRow>
        | SorterResult<CasinoProviderRow>[],
    ) => {
      const one = Array.isArray(sorter) ? sorter[0] : sorter;
      const field = one?.field as CasinoProviderSortKey | undefined;
      setSortState({
        field: field ?? null,
        order: one?.order ?? null,
      });
      setCurrentPage(1);
    },
    [],
  );

  const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    setCurrentPage(1);
  }, []);

  const handleDateRangeQuery = useCallback(() => {
    setCurrentPage(1);
  }, []);

  const handleApplyFilters = useCallback(() => {
    setAppliedMoneyTypes(draftMoneyTypes);
    setCurrentPage(1);
  }, [draftMoneyTypes]);

  const handleClearFilters = useCallback(() => {
    setDraftMoneyTypes([]);
    setAppliedMoneyTypes([]);
    setCurrentPage(1);
  }, []);

  const handleExport = useCallback(() => {
    if (displayRows.length === 0) {
      message.warning(AppStrings.CASINO_PROVIDERS_EXPORT_NO_ROWS);
      return;
    }
    try {
      const csv = buildCasinoProvidersCsv(displayRows);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "casino-providers-report.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[CasinoProvidersExport]", err);
      message.error(AppStrings.CASINO_PROVIDERS_EXPORT_ERROR);
    }
  }, [displayRows]);

  return (
    <>
      <div className="casino-providers-page">
        <div className="casino-providers-header">
          <div className="casino-providers-header-controls manual-actions-header-controls">
            <CustomDatePicker
              dates={dates}
              setDates={setDates}
              getData={handleDateRangeQuery}
              showPresets={true}
              startRange={AppStrings.THIS_MONTH}
            />
            <button
              type="button"
              className="download-button"
              aria-label="Download"
              onClick={handleExport}
            >
              <DownloadOutlined />
            </button>
          </div>
        </div>

        <div className="casino-providers-search-row">
          <Input
            variant="search"
            allowClear
            className="casino-providers-search-input"
            placeholder={AppStrings.CASINO_PROVIDERS_SEARCH_PLACEHOLDER}
            value={searchText}
            onChange={handleSearchChange}
            aria-label={AppStrings.CASINO_PROVIDERS_SEARCH_PLACEHOLDER}
          />
          <button
            type="button"
            className="casino-providers-filter-toggle"
            aria-expanded={filtersExpanded}
            aria-label={AppStrings.PAM_REPORT_FILTERS}
            onClick={() => setFiltersExpanded((expanded) => !expanded)}
          >
            <Filter size={18} aria-hidden />
            {filtersExpanded ? (
              <ChevronUp size={18} aria-hidden />
            ) : (
              <ChevronDown size={18} aria-hidden />
            )}
          </button>
        </div>

        {filtersExpanded && (
        <div className="casino-providers-filters">
          <div className="casino-providers-filter-field">
            <span className="casino-providers-filter-label">
              {AppStrings.CASINO_KPI_CURRENCY_TYPE}
            </span>
            <SharedSelect<string[]>
              mode="multiple"
              showSearch
              optionFilterProp="label"
              placeholder={AppStrings.CASINO_KPI_ALL_CURRENCY_TYPES}
              aria-label={AppStrings.CASINO_KPI_CURRENCY_TYPE}
              className="casino-providers-filter-select"
              options={currencyTypeOptions}
              value={draftMoneyTypes}
              onChange={(values) =>
                setDraftMoneyTypes(values.map((value) => String(value)))
              }
              maxTagCount="responsive"
            />
          </div>
          <div className="casino-providers-filter-actions">
            <button type="button" onClick={handleClearFilters}>
              {AppStrings.CLEAR_FILTERS}
            </button>
            <button
              type="button"
              className="apply"
              disabled={!filtersDirty}
              onClick={handleApplyFilters}
            >
              {AppStrings.APPLY}
            </button>
          </div>
        </div>
        )}

        <div className="casino-providers-table-section">
          {/*
            Match Reporting Summary: toast on failure; table shows empty "No data";
            only the grid is in Spin while fetching.
          */}
          <Spin spinning={isFetching}>
            <DataTable
              id="casino-providers-table"
              rowKey="key"
              className="casino-finance-table casino-providers-table"
              columns={columns}
              dataSource={displayRows}
              pagination={false}
              onChange={handleTableChange}
              showSorterTooltip={{ target: "sorter-icon" }}
              rowClassName={(_record: CasinoProviderRow, index: number) =>
                index % 2 === 1 ? "casino-finance-table-row--alt" : ""
              }
              scrollPreset="verticalOnly"
              locale={{ emptyText: tableEmptyNode }}
            />
          </Spin>
          <div className="casino-providers-pagination-bar">
            <div className="casino-providers-pagination-left">
              <Select
                value={pageSize}
                aria-label={AppStrings.REPORTING_PAGINATION_PER_PAGE}
                onChange={(v) => {
                  setPageSize(normalizeAntSelectNumberValue(v));
                  setCurrentPage(1);
                }}
                className="casino-providers-page-size-select"
                options={PAGE_SIZE_OPTIONS.map((n) => ({
                  value: n,
                  label: String(n),
                }))}
              />
              <span className="casino-providers-per-page-label">
                {AppStrings.REPORTING_PAGINATION_PER_PAGE}{" "}
                {AppStrings.CASINO_PROVIDERS_PAGINATION_TOTAL_LABEL.replace(
                  "{total}",
                  String(displayTotal),
                )}
              </span>
            </div>
            <div className="casino-providers-pagination-right">
              <Pagination
                current={currentPage}
                total={displayTotal}
                pageSize={pageSize}
                showSizeChanger={false}
                onChange={(p) => setCurrentPage(p)}
                itemRender={(_pageNum, type, originalElement) => {
                  if (type === "prev") {
                    return (
                      <span className="casino-providers-pagination-nav">
                        {originalElement}
                        <span>{AppStrings.BACK}</span>
                      </span>
                    );
                  }
                  if (type === "next") {
                    return (
                      <span className="casino-providers-pagination-nav">
                        <span>{AppStrings.NEXT}</span>
                        {originalElement}
                      </span>
                    );
                  }
                  return originalElement;
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default CasinoProvidersReports;
